// src/views/menage/SectionSignalerManque.jsx — « IL MANQUE QUELQUE CHOSE ? »
// (recalage Dimitri 2026-08-21) : l'agent COCHE ce qui manque dans une liste —
// PAS de phrase à rédiger — puis choisit l'échéance :
//   · « pour cette arrivée »     — le voyageur arrive sans ;
//   · « pour la prochaine fois » — attend le ménage suivant.
// Jamais de défaut : rien ne part sans choix. UNE donnée (stock_signalements),
// les écrans du contrôleur, du Suivi et du Stock lisent la même chose.

import { useState } from 'react'
import { DS } from '../../lib/ui'
import { signalerManque } from '../../lib/stock-signalements-api'
import { splitConsommables } from '../../lib/consommables-helpers'
import { Megaphone, AlarmClock, CalendarClock, Check, Loader2 } from 'lucide-react'

const TERRA = '#b8412b'
const AMBRE = '#d4a24c'

export default function SectionSignalerManque({ cfg, logementId }) {
  const [ouvert, setOuvert] = useState(false)
  const [echeance, setEcheance] = useState(null) // JAMAIS de défaut — il choisit.
  const [coches, setCoches] = useState({}) // { csId: true }
  const [envoi, setEnvoi] = useState(false)
  const [erreur, setErreur] = useState(null)
  const [envoyes, setEnvoyes] = useState([]) // rappel local de la session

  const { kitProduits, operProduits } = splitConsommables(cfg)
  const produits = [...kitProduits, ...operProduits]
  const choisis = produits.filter((p) => coches[p.id])
  const pret = echeance && choisis.length > 0

  const envoyer = async () => {
    if (!pret || envoi) return
    setEnvoi(true); setErreur(null)
    try {
      for (const p of choisis) {
        await signalerManque({ logementId, echeance, csId: p.id, texte: null })
      }
      setEnvoyes((prev) => [...prev, { noms: choisis.map((p) => p.nom).join(', '), echeance }])
      setCoches({}); setEcheance(null)
    } catch (e) {
      setErreur(e?.message || 'Signalement échoué — réessayez.')
    } finally { setEnvoi(false) }
  }

  const boutonEcheance = (cle, Ico, label, teinte) => {
    const actif = echeance === cle
    return (
      <button key={cle} type="button" data-testid={`manque-echeance-${cle}`}
        aria-pressed={actif} onClick={() => setEcheance(cle)} className="fbz-press"
        style={{ flex: 1, minHeight: 40, display: 'inline-flex', alignItems: 'center',
          justifyContent: 'center', gap: 6, borderRadius: 9, fontSize: 11.5, fontWeight: 700,
          fontFamily: DS.fontBody, cursor: 'pointer',
          border: `1.5px solid ${actif ? teinte : DS.border}`,
          background: actif ? `${teinte}18` : DS.card,
          color: actif ? teinte : DS.textSecondary }}>
        <Ico size={14} strokeWidth={2.2} /> {label}
      </button>
    )
  }

  return (
    <div data-testid="section-signaler-manque"
      style={{ marginTop: 10, border: `1px solid ${DS.border}`, borderRadius: DS.radiusCard, overflow: 'hidden' }}>
      <button type="button" data-testid="manque-toggle" onClick={() => setOuvert((v) => !v)}
        aria-expanded={ouvert}
        style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between',
          gap: 8, padding: '12px 14px', border: 'none', background: '#fdf9f7', cursor: 'pointer',
          fontFamily: DS.fontBody, textAlign: 'left' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13,
          fontWeight: 700, color: '#8f2f1d' }}>
          <Megaphone size={15} strokeWidth={2.2} /> Il manque quelque chose ?
        </span>
        <span style={{ fontSize: 10, color: DS.textMuted }}>facultatif</span>
      </button>

      {ouvert && (
        <div style={{ padding: '12px 14px' }}>
          {/* 1. IL COCHE CE QUI MANQUE — une liste, pas une phrase. */}
          <div data-testid="manque-liste"
            style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {produits.map((p) => {
              const actif = !!coches[p.id]
              return (
                <button key={p.id} type="button" data-testid={`manque-produit-${p.id}`}
                  aria-pressed={actif}
                  onClick={() => setCoches((c) => ({ ...c, [p.id]: !c[p.id] }))}
                  className="fbz-press"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5,
                    minHeight: 34, padding: '4px 11px', borderRadius: 9, fontSize: 11.5,
                    fontWeight: actif ? 700 : 500, fontFamily: DS.fontBody, cursor: 'pointer',
                    border: `1.5px solid ${actif ? '#8f2f1d' : DS.border}`,
                    background: actif ? '#fdf4f0' : DS.card,
                    color: actif ? '#8f2f1d' : DS.textSecondary }}>
                  {actif && <Check size={12} strokeWidth={2.6} />} {p.nom}
                </button>
              )
            })}
          </div>

          {/* 2. POUR QUAND — il CHOISIT, jamais un défaut. */}
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            {boutonEcheance('aujourdhui', AlarmClock, 'Pour cette arrivée', TERRA)}
            {boutonEcheance('prochaine_fois', CalendarClock, 'Pour la prochaine fois', AMBRE)}
          </div>
          {echeance === 'aujourdhui' && (
            <div style={{ fontSize: 10.5, color: '#8f2f1d', marginTop: 5 }}>
              Le voyageur arrive sans — quelqu'un corrigera avant son arrivée.
            </div>
          )}

          {erreur && (
            <div style={{ marginTop: 8, fontSize: 12, color: DS.dangerText, background: DS.dangerBg,
              borderRadius: 8, padding: '8px 11px' }}>{erreur}</div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
            <button type="button" data-testid="manque-envoyer" disabled={!pret || envoi}
              onClick={envoyer} className="fbz-press"
              style={{ minHeight: 40, padding: '0 16px', display: 'inline-flex', alignItems: 'center',
                gap: 7, border: 'none', borderRadius: 9, fontSize: 12.5, fontWeight: 700,
                fontFamily: DS.fontBody, cursor: pret && !envoi ? 'pointer' : 'not-allowed',
                background: pret ? '#8f2f1d' : '#eef1f4', color: pret ? '#fff' : '#98a2ad' }}>
              {envoi ? <Loader2 size={14} /> : <Megaphone size={14} strokeWidth={2.2} />}
              Signaler{choisis.length > 1 ? ` ${choisis.length} manques` : ''}
            </button>
          </div>

          {envoyes.length > 0 && (
            <div data-testid="manque-envoyes" style={{ marginTop: 10 }}>
              {envoyes.map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6,
                  fontSize: 11.5, color: '#1a4f3a', marginTop: 3 }}>
                  <Check size={13} strokeWidth={2.4} color="#7fc4a3" />
                  Signalé : {s.noms} — {s.echeance === 'aujourdhui' ? 'pour cette arrivée' : 'pour la prochaine fois'}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
